/*
==========================================
STAMP BODY COMPOSER  v1.5.0
文件: assets/stamp-body-composer.js

v1.5.0 改动：
- [FIX] 移除 StampPhotoEditor 委托，改为直接事件绑定
  与产品8（stamp-address-composer）同款方案，彻底解决 Move/Erase 不工作的问题
  内部自行管理 _headCanvas / _toolMode / _brushSize / offset / scale
  _bindEvents() 直接绑定 canvas，不依赖任何外部模块

v1.3.2（保留）：
- 弧形文字方向修复
- 图片顶边框修复（dy=150）
==========================================
*/

window.StampBodyComposer = (function () {
  'use strict';

  let _templateImg    = null;
  let _slotConfig     = null;
  let _composerCanvas = null;
  let _composerCtx    = null;
  let _outputW        = 600;
  let _outputH        = 700;
  let _templateDrawBottom = 580;
  let _originalHeadUrl = null;

  // ── 照片状态（直接管理）──
  let _headCanvas  = null;   // 离屏 canvas，存储处理后的照片像素
  let _originalImg = null;   // 原始图像，用于 reset
  let _headOffsetX = 0;
  let _headOffsetY = 0;
  let _headScale   = 1.5;

  let _toolMode  = 'move';   // 'move' | 'eraser'
  let _brushSize = 20;
  let _isDrawing = false;
  let _lastX     = 0, _lastY = 0;
  let _dragging  = false;
  let _dragStartX = 0, _dragStartY = 0;
  let _dragStartOX = 0, _dragStartOY = 0;

  // Custom Text 状态
  let _customText     = '';
  let _customFont     = 'Montserrat';
  let _customFontSize = 36;
  let _customColor    = '#000000';
  let _textOffsetX    = 0;
  let _textOffsetY    = 0;
  let _textCurveDeg   = 25;

  // 文字拖拽状态
  let _textDragging    = false;
  let _textDragStartX  = 0, _textDragStartY  = 0;
  let _textDragStartOX = 0, _textDragStartOY = 0;

  // ── 坐标工具 ──
  function _getSlotCoords() {
    if (!_slotConfig) return null;
    const iw = _templateImg ? (_templateImg.naturalWidth  || _templateImg.width)  : _outputW;
    const ih = _templateImg ? (_templateImg.naturalHeight || _templateImg.height) : _outputH;
    const tScale = Math.min(_outputW / iw, (_outputH - 100) / ih);
    const dw = iw * tScale, dh = ih * tScale;
    const dx = (_outputW - dw) / 2;
    const dy = 150;
    const slotCX = dx + _slotConfig.cx_frac * dw;
    const slotCY = dy + _slotConfig.cy_frac * dh;
    const slotR  = _slotConfig.r_frac * dw;
    return { slotCX, slotCY, slotR };
  }

  function _getPos(e) {
    const rect = _composerCanvas.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return {
      x: (t.clientX - rect.left) * (_outputW / rect.width),
      y: (t.clientY - rect.top)  * (_outputH / rect.height)
    };
  }

  // ── 橡皮擦 ──
  function _erase(cx, cy) {
    if (!_headCanvas) return;
    const sc = _getSlotCoords();
    if (!sc) return;
    const headDiam = sc.slotR * 2 * _headScale * 0.97;
    const hx = sc.slotCX - headDiam / 2 + _headOffsetX;
    const hy = sc.slotCY - headDiam / 2 + _headOffsetY;
    const sx = _headCanvas.width  / headDiam;
    const sy = _headCanvas.height / headDiam;
    const lr = (_brushSize / 2) * Math.max(sx, sy);
    const hctx = _headCanvas.getContext('2d');
    hctx.save();
    hctx.globalCompositeOperation = 'destination-out';
    hctx.beginPath();
    hctx.arc((cx - hx) * sx, (cy - hy) * sy, lr, 0, Math.PI * 2);
    hctx.fill();
    hctx.restore();
    _render();
  }

  function _interpolate(x1, y1, x2, y2) {
    const steps = Math.max(1, Math.ceil(Math.hypot(x2 - x1, y2 - y1) / (_brushSize * 0.25)));
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      _erase(x1 + (x2 - x1) * t, y1 + (y2 - y1) * t);
    }
  }

  // ── 事件绑定（直接绑定，不依赖 StampPhotoEditor）──
  function _bindEvents() {
    if (!_composerCanvas) return;
    _composerCanvas.style.touchAction = 'none';
    _composerCanvas.style.userSelect  = 'none';
    _composerCanvas.style.cursor      = 'grab';

    const onStart = (e) => {
      e.preventDefault();
      const pos = _getPos(e);
      if (_toolMode === 'eraser') {
        _isDrawing = true; _lastX = pos.x; _lastY = pos.y; _erase(pos.x, pos.y);
      } else {
        if (_isNearText(pos.x, pos.y)) return;
        _dragging = true;
        _dragStartX = pos.x; _dragStartY = pos.y;
        _dragStartOX = _headOffsetX; _dragStartOY = _headOffsetY;
        _composerCanvas.style.cursor = 'grabbing';
      }
    };

    const onMove = (e) => {
      if (!_isDrawing && !_dragging) return;
      e.preventDefault();
      const pos = _getPos(e);
      if (_toolMode === 'eraser' && _isDrawing) {
        _interpolate(_lastX, _lastY, pos.x, pos.y);
        _lastX = pos.x; _lastY = pos.y;
      } else if (_toolMode === 'move' && _dragging) {
        _headOffsetX = _dragStartOX + (pos.x - _dragStartX);
        _headOffsetY = _dragStartOY + (pos.y - _dragStartY);
        _render();
      }
    };

    const onEnd = () => {
      _isDrawing = false; _dragging = false;
      if (_composerCanvas) _composerCanvas.style.cursor = _toolMode === 'move' ? 'grab' : 'crosshair';
    };

    _composerCanvas.addEventListener('mousedown',  e => { e.preventDefault(); onStart(e); });
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup',   onEnd);
    _composerCanvas.addEventListener('touchstart', onStart, { passive: false });
    _composerCanvas.addEventListener('touchmove',  onMove,  { passive: false });
    document.addEventListener('touchend',  onEnd);
  }

  // ── init ──
  async function init(config, outputCanvasId) {
    const bt = config.body_template;
    if (!bt || !bt.url) { console.error('[BodyComposer] body_template.url missing'); return false; }

    _slotConfig = bt.head_slot;

    _composerCanvas = document.getElementById(outputCanvasId + '-composer')
                   || document.getElementById(outputCanvasId);
    if (!_composerCanvas || _composerCanvas.tagName !== 'CANVAS') {
      console.error('[BodyComposer] canvas not found for:', outputCanvasId); return false;
    }
    _composerCanvas.width  = _outputW;
    _composerCanvas.height = _outputH;
    _composerCtx = _composerCanvas.getContext('2d');

    _bindEvents();

    _templateImg = await _loadImage(bt.url);
    if (!_templateImg) return false;

    _render();
    return true;
  }

  // ── setHead ──
  async function setHead(headDataUrl) {
    _originalHeadUrl = headDataUrl;
    const img = await _loadImage(headDataUrl);
    if (!img) return;
    _originalImg = img;
    _headCanvas  = document.createElement('canvas');
    _headCanvas.width  = img.width;
    _headCanvas.height = img.height;
    _headCanvas.getContext('2d').drawImage(img, 0, 0);
    _headOffsetX = 0; _headOffsetY = 0;
    _render();
  }

  // ── _render ──
  function _render() {
    if (!_composerCtx) return;
    const ctx = _composerCtx;
    const W = _outputW, H = _outputH;

    ctx.clearRect(0, 0, W, H);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, W, H);

    // 先画头像（在模板下方，被模板遮住边缘）
    const sc = _getSlotCoords();
    if (_headCanvas && sc) {
      const headDiam = sc.slotR * 2 * _headScale * 0.97;
      const hx = sc.slotCX - headDiam / 2 + _headOffsetX;
      const hy = sc.slotCY - headDiam / 2 + _headOffsetY;
      ctx.drawImage(_headCanvas, hx, hy, headDiam, headDiam);
    }

    // 再画身体模板（覆盖在头像上）
    if (_templateImg) {
      const iw = _templateImg.naturalWidth  || _templateImg.width;
      const ih = _templateImg.naturalHeight || _templateImg.height;
      const scale = Math.min(_outputW / iw, (_outputH - 100) / ih);
      const dw = iw * scale, dh = ih * scale;
      const dx = (W - dw) / 2;
      const dy = 150;
      _templateDrawBottom = dy + dh;
      ctx.drawImage(_templateImg, dx, dy, dw, dh);
    }

    _renderText(ctx);
    _syncToPreview();
  }

  // ── _syncToPreview ──
  function _syncToPreview() {
    ['prodStampCanvas-placeholder', 'prodMobileCanvasBottom-placeholder'].forEach(id => {
      const el = document.getElementById(id);
      if (el) el.style.display = 'none';
    });

    const mobileCanvas = document.getElementById('prodMobileCanvasBottom-composer');
    if (mobileCanvas && mobileCanvas !== _composerCanvas) {
      mobileCanvas.width  = _outputW;
      mobileCanvas.height = _outputH;
      mobileCanvas.getContext('2d').drawImage(_composerCanvas, 0, 0);
    }

    if (window._extraMobileCanvasIds && Array.isArray(window._extraMobileCanvasIds)) {
      window._extraMobileCanvasIds.forEach(id => {
        const extra = document.getElementById(id);
        if (extra && extra !== _composerCanvas) {
          extra.width  = _outputW; extra.height = _outputH;
          extra.getContext('2d').drawImage(_composerCanvas, 0, 0);
          extra.style.display = 'block';
          const ph = document.getElementById('prodMobileAboveUploadPlaceholder');
          if (ph) ph.style.display = 'none';
        }
      });
    }
    if (window._extraMobileCanvasId) {
      const extra = document.getElementById(window._extraMobileCanvasId);
      if (extra && extra !== _composerCanvas) {
        extra.width  = _outputW; extra.height = _outputH;
        extra.getContext('2d').drawImage(_composerCanvas, 0, 0);
      }
    }
  }

  // ── 弧形文字渲染 ──
  function _renderCurvedText(ctx, text, baseX, baseY, fontSize, fontFamily, color, curveDeg) {
    const rad = (Math.abs(curveDeg) * Math.PI) / 180;
    ctx.save();
    ctx.font = `bold ${fontSize}px ${fontFamily}, sans-serif`;
    const totalW = ctx.measureText(text).width;
    ctx.restore();
    if (totalW < 1) return;

    const arcRadius = totalW / (2 * Math.sin(rad / 2));
    const sign = curveDeg > 0 ? 1 : -1;
    const arcCX = baseX;
    const arcCY = baseY + sign * arcRadius;
    const startAngle = -Math.PI / 2 - rad / 2;
    const angleStep  = rad / Math.max(text.length - 1, 1);

    ctx.save();
    ctx.font = `bold ${fontSize}px ${fontFamily}, sans-serif`;
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.strokeStyle = 'rgba(255,255,255,0.75)'; ctx.lineWidth = 5;
    ctx.fillStyle = color;

    for (let i = 0; i < text.length; i++) {
      const angle = startAngle + i * angleStep + angleStep / 2;
      const charX = arcCX + arcRadius * Math.cos(angle);
      const charY = arcCY + arcRadius * Math.sin(angle);
      ctx.save();
      ctx.translate(charX, charY);
      ctx.rotate(angle + Math.PI / 2);
      ctx.strokeText(text[i], 0, 0);
      ctx.fillText(text[i], 0, 0);
      ctx.restore();
    }
    ctx.restore();
  }

  function _renderText(ctx) {
    if (!_customText.trim()) return;
    const baseX = _outputW / 2 - 30 + _textOffsetX;
    const baseY = 48 + _textOffsetY;
    const fontFamily = _customFont.includes(' ') ? `'${_customFont}'` : _customFont;

    if (Math.abs(_textCurveDeg) < 2) {
      ctx.save();
      ctx.font = `bold ${_customFontSize}px ${fontFamily}, sans-serif`;
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.strokeStyle = 'rgba(255,255,255,0.7)'; ctx.lineWidth = 4;
      ctx.strokeText(_customText, baseX, baseY);
      ctx.fillStyle = _customColor;
      ctx.fillText(_customText, baseX, baseY);
      ctx.restore();
    } else {
      _renderCurvedText(ctx, _customText, baseX, baseY, _customFontSize, fontFamily, _customColor, _textCurveDeg);
    }
  }

  function _isNearText(cx, cy) {
    if (!_customText.trim()) return false;
    const baseX = _outputW / 2 + _textOffsetX;
    const baseY = 48 + _textOffsetY;
    const hitW  = Math.max(200, _customText.length * _customFontSize * 0.65);
    const hitH  = _customFontSize * 2 + 30;
    return cx >= baseX - hitW / 2 && cx <= baseX + hitW / 2 &&
           cy >= baseY - hitH     && cy <= baseY + hitH / 2;
  }

  // ── resetEraser ──
  async function resetEraser() {
    if (!_originalImg) return;
    const ox = _headOffsetX, oy = _headOffsetY;
    _headCanvas = document.createElement('canvas');
    _headCanvas.width  = _originalImg.width;
    _headCanvas.height = _originalImg.height;
    _headCanvas.getContext('2d').drawImage(_originalImg, 0, 0);
    _headOffsetX = ox; _headOffsetY = oy;
    _render();
  }

  // ── 工具切换 ──
  function setTool(mode) {
    _toolMode = mode;
    if (_composerCanvas) _composerCanvas.style.cursor = mode === 'move' ? 'grab' : 'crosshair';
  }
  function setEraserActive(active) { setTool(active ? 'eraser' : 'move'); }
  function setBrushSize(size)      { _brushSize = size; }
  function setEraserSize(size)     { _brushSize = size; }

  // ── 头像位移/缩放 ──
  function moveHead(dx, dy)    { _headOffsetX += dx; _headOffsetY += dy; _render(); }
  function setHeadOffset(x, y) { _headOffsetX = x; _headOffsetY = y; _render(); }
  function setHeadScale(scale) { _headScale = Math.max(0.3, Math.min(3.0, scale)); _render(); }

  function getComposedDataUrl() {
    return _composerCanvas ? _composerCanvas.toDataURL('image/png') : null;
  }

  // ── enableHeadDrag（保留兼容，事件已在 _bindEvents 中处理）──
  function enableHeadDrag() {
    if (_composerCanvas) _composerCanvas.style.cursor = 'grab';
  }

  // ── enableTextDrag ──
  function enableTextDrag() {
    if (!_composerCanvas || window._bodyComposerTextDragAttached) return;
    window._bodyComposerTextDragAttached = true;

    const getPos = (e) => {
      const rect = _composerCanvas.getBoundingClientRect();
      const t = e.touches ? e.touches[0] : e;
      return {
        x: (t.clientX - rect.left) * (_outputW / rect.width),
        y: (t.clientY - rect.top)  * (_outputH / rect.height)
      };
    };

    const onStart = (e) => {
      const pos = getPos(e);
      if (!_isNearText(pos.x, pos.y)) return;
      _textDragging = true;
      _textDragStartX = pos.x; _textDragStartY = pos.y;
      _textDragStartOX = _textOffsetX; _textDragStartOY = _textOffsetY;
      e.stopPropagation();
    };

    document.addEventListener('mousemove', (e) => {
      if (!_textDragging) return;
      const pos = getPos(e);
      _textOffsetX = _textDragStartOX + (pos.x - _textDragStartX);
      _textOffsetY = _textDragStartOY + (pos.y - _textDragStartY);
      _render();
    });

    document.addEventListener('touchmove', (e) => {
      if (!_textDragging) return;
      e.preventDefault();
      const pos = getPos(e);
      _textOffsetX = _textDragStartOX + (pos.x - _textDragStartX);
      _textOffsetY = _textDragStartOY + (pos.y - _textDragStartY);
      _render();
    }, { passive: false });

    const onEnd = () => { _textDragging = false; };
    document.addEventListener('mouseup',  onEnd);
    document.addEventListener('touchend', onEnd);
    _composerCanvas.addEventListener('mousedown',  e => { onStart(e); });
    _composerCanvas.addEventListener('touchstart', e => { onStart(e); }, { passive: true });
  }

  // ── Custom Text API ──
  function setCustomText(text)     { _customText     = text;  _render(); }
  function setCustomFont(font)     { _customFont     = font;  _render(); }
  function setCustomFontSize(px)   { _customFontSize = px;    _render(); }
  function setCustomColor(color)   { _customColor    = color; _render(); }
  function setCustomTextCurve(deg) { _textCurveDeg   = deg;   _render(); }

  // ── initUploadZone ──
  function initUploadZone(selector, processCallback) {
    const zones = document.querySelectorAll(selector);
    if (!zones.length) { console.warn('[BodyComposer] initUploadZone: no elements for', selector); return; }
    zones.forEach(zone => {
      if (zone._bcBound) return;
      zone._bcBound = true;
      zone.addEventListener('click', () => {
        const input = document.createElement('input');
        input.type = 'file'; input.accept = 'image/jpeg,image/png,image/webp';
        input.onchange = async (e) => {
          const file = e.target.files[0]; if (!file) return;
          zone.classList.add('loading');
          try { const url = await processCallback(file); if (url) await setHead(url); }
          catch (err) { console.error('[BodyComposer] Upload error:', err); }
          finally { zone.classList.remove('loading'); }
        };
        input.click();
      });
      zone.addEventListener('dragover',  e => { e.preventDefault(); zone.classList.add('drag-over'); });
      zone.addEventListener('dragleave', () => zone.classList.remove('drag-over'));
      zone.addEventListener('drop', async e => {
        e.preventDefault(); zone.classList.remove('drag-over');
        const file = e.dataTransfer.files[0]; if (!file) return;
        zone.classList.add('loading');
        try { const url = await processCallback(file); if (url) await setHead(url); }
        catch (err) { console.error('[BodyComposer] Drop error:', err); }
        finally { zone.classList.remove('loading'); }
      });
    });
  }

  function _loadImage(url) {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload  = () => resolve(img);
      img.onerror = () => { console.error('[BodyComposer] Failed to load:', url); resolve(null); };
      img.src = url;
    });
  }

  return {
    init, setHead, setTool, setEraserActive,
    setBrushSize, setEraserSize, resetEraser,
    moveHead, setHeadOffset, setHeadScale,
    enableHeadDrag, enableTextDrag, initUploadZone,
    getComposedDataUrl, render: _render,
    setCustomText, setCustomFont, setCustomFontSize, setCustomColor,
    setCustomTextCurve
  };

})();